import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Descriptions, Empty, Row, Segmented, Select, Space, Spin, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useProjectsForSelection } from '@/hooks/useProject';
import {
  getProjectLedger,
  type ProjectLedgerAccount,
  type ProjectLedgerLine,
  type ProjectLedgerReport,
} from '@/services/financialReports.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import BranchFilterSelect from '@/components/finance/report-document/BranchFilterSelect';
import { formatDate, formatMoney, formatPercent, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format';
import { getLedgerReferenceLabel, getVoucherLink } from '@/utils/financeVoucherLinks.util';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

type Scope = 'all' | 'pl' | 'bs';
type View = 'account' | 'chronological';

const SECTION_TAG: Record<string, { labelKey: string; color: string }> = {
  revenue: { labelKey: 'sectionRevenue', color: 'green' },
  direct_cost: { labelKey: 'sectionDirectCost', color: 'volcano' },
  overhead: { labelKey: 'sectionOverhead', color: 'orange' },
  other_income: { labelKey: 'sectionOtherIncome', color: 'cyan' },
};

const REFERENCE_TYPE_ALIASES: Record<string, string> = {
  GRN: 'GoodsReceiptNote',
  goods_receipt_note: 'GoodsReceiptNote',
  supplier_invoice: 'SupplierInvoice',
};

function amt(v: number | null | undefined): string {
  return formatMoney(v ?? 0, PRINT_PLAIN_AMOUNT_PROFILE);
}

function lineLink(line: ProjectLedgerLine): string {
  const referenceType = line.reference_type ? REFERENCE_TYPE_ALIASES[line.reference_type] ?? line.reference_type : undefined;
  return getVoucherLink({ ...line, reference_type: referenceType });
}

function yearToDate(): [Dayjs, Dayjs] {
  return [dayjs().startOf('year'), dayjs()];
}

const ProjectLedgerPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = searchParams.get('project') || undefined;
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => {
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    return from && to ? [dayjs(from), dayjs(to)] : yearToDate();
  });
  const [scope, setScope] = useState<Scope>((searchParams.get('scope') as Scope) || 'all');
  const [view, setView] = useState<View>('account');
  const [branchId, setBranchId] = useState<string | undefined>(searchParams.get('branch') || undefined);

  const { data: projects = [], isLoading: loadingProjects } = useProjectsForSelection();
  const fromDate = range[0].format('YYYY-MM-DD');
  const toDate = range[1].format('YYYY-MM-DD');

  const { data: report, isFetching, error } = useQuery({
    queryKey: ['fin-project-ledger', projectId, fromDate, toDate, scope, branchId],
    queryFn: () => getProjectLedger({ project_id: projectId!, from_date: fromDate, to_date: toDate, scope, ...(branchId ? { branch_id: branchId } : {}) }),
    enabled: Boolean(projectId),
  });
  const docParams = projectId
    ? { project_id: projectId, from: fromDate, to: toDate, scope, ...(branchId ? { branch_id: branchId } : {}) }
    : null;

  const setProject = (id?: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('project', id);
    else next.delete('project');
    setSearchParams(next, { replace: true });
  };

  const linesByAccount = useMemo(() => {
    const map = new Map<string, ProjectLedgerLine[]>();
    for (const line of report?.lines ?? []) {
      const list = map.get(line.account_id) ?? [];
      list.push(line);
      map.set(line.account_id, list);
    }
    return map;
  }, [report]);

  const errorMessage = (error as any)?.response?.data?.message || (error as Error | null)?.message;

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Row justify="space-between" align="middle" gutter={[12, 12]}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            {t('projectLedger')}
          </Title>
          <Text type="secondary">{t('projectLedgerHint')}</Text>
        </Col>
        <Col>
          <ReportDocumentActions reportKey="project-ledger" params={docParams} />
        </Col>
      </Row>

      <Card size="small">
        <Space wrap>
          <Select
            showSearch
            allowClear
            style={{ minWidth: 420 }}
            placeholder={t('selectProject')}
            loading={loadingProjects}
            value={projectId}
            onChange={(v?: string) => setProject(v)}
            optionFilterProp="label"
            options={projects.map((p) => ({
              value: p.id,
              label: `${p.project_number} — ${p.project_name}${p.customer_name ? ` (${p.customer_name})` : ''}`,
            }))}
          />
          <RangePicker
            value={range}
            allowClear={false}
            format="DD MMM YYYY"
            onChange={(v) => v?.[0] && v?.[1] && setRange([v[0], v[1]])}
          />
          <Segmented
            value={scope}
            onChange={(v) => setScope(v as Scope)}
            options={[
              { value: 'all', label: t('allAccountsScope') },
              { value: 'pl', label: t('plOnly') },
              { value: 'bs', label: t('balanceSheetOnly') },
            ]}
          />
          <BranchFilterSelect value={branchId} onChange={setBranchId} />
          <Segmented
            value={view}
            onChange={(v) => setView(v as View)}
            options={[
              { value: 'account', label: t('byAccount') },
              { value: 'chronological', label: t('dateOrder') },
            ]}
          />
        </Space>
      </Card>

      {!projectId ? (
        <Card>
          <Empty description={t('selectProjectForLedger')} />
        </Card>
      ) : isFetching && !report ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={t('couldNotLoadProjectLedger')} description={errorMessage} />
      ) : report ? (
        <Spin spinning={isFetching}>
          <LedgerBody report={report} view={view} linesByAccount={linesByAccount} />
        </Spin>
      ) : null}
    </div>
  );
};

const LedgerBody: React.FC<{
  report: ProjectLedgerReport;
  view: View;
  linesByAccount: Map<string, ProjectLedgerLine[]>;
  printMode?: boolean;
}> = ({ report, view, linesByAccount, printMode }) => {
  const { t } = useTranslation('finance');
  const { project, period_pnl: pnl } = report;
  const tableProps = { size: 'small' as const, bordered: true, pagination: false as const };

  const lineColumns: ColumnsType<ProjectLedgerLine> = [
    { title: t('date'), dataIndex: 'voucher_date', width: 100, render: (v: string) => formatDate(v) || v },
    {
      title: t('document'),
      key: 'document',
      width: 150,
      render: (_: unknown, l) =>
        printMode ? getLedgerReferenceLabel(l) : <Link to={lineLink(l)}>{getLedgerReferenceLabel(l)}</Link>,
    },
    { title: t('voucher'), dataIndex: 'voucher_number', width: 130 },
    ...(view === 'chronological'
      ? [
          {
            title: t('account'),
            key: 'account',
            width: 220,
            render: (_: unknown, l: ProjectLedgerLine) => `${l.account_code} ${l.account_name}`,
          },
        ]
      : []),
    { title: t('description'), dataIndex: 'description', ellipsis: !printMode },
    {
      title: t('debit'),
      dataIndex: 'debit_amount',
      align: 'right',
      width: 120,
      render: (v: number) => (v ? amt(v) : ''),
    },
    {
      title: t('credit'),
      dataIndex: 'credit_amount',
      align: 'right',
      width: 120,
      render: (v: number) => (v ? amt(v) : ''),
    },
  ];

  const accountColumns: ColumnsType<ProjectLedgerAccount> = [
    { title: t('code'), dataIndex: 'account_code', width: 100 },
    { title: t('account'), dataIndex: 'account_name' },
    {
      title: t('type'),
      key: 'type',
      width: 130,
      render: (_: unknown, a) =>
        a.pnl_section && SECTION_TAG[a.pnl_section] ? (
          <Tag color={SECTION_TAG[a.pnl_section].color}>{t(SECTION_TAG[a.pnl_section].labelKey)}</Tag>
        ) : (
          <Tag>{a.account_type}</Tag>
        ),
    },
    { title: t('opening'), dataIndex: 'opening_balance', align: 'right', width: 120, render: (v: number) => amt(v) },
    { title: t('debit'), dataIndex: 'debit', align: 'right', width: 120, render: (v: number) => amt(v) },
    { title: t('credit'), dataIndex: 'credit', align: 'right', width: 120, render: (v: number) => amt(v) },
    { title: t('closing'), dataIndex: 'closing_balance', align: 'right', width: 120, render: (v: number) => amt(v) },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {printMode ? (
        <Title level={4} style={{ margin: 0 }}>
          {t('projectLedger')}
        </Title>
      ) : null}
      <Descriptions size="small" bordered column={printMode ? 3 : { xs: 1, sm: 2, lg: 3 }}>
        <Descriptions.Item label={t('project')}>
          {project.project_number} — {project.project_name}
        </Descriptions.Item>
        <Descriptions.Item label={t('customer')}>{project.customer_name || '—'}</Descriptions.Item>
        <Descriptions.Item label={t('period')}>
          {formatDate(report.from_date)} – {formatDate(report.to_date)}
        </Descriptions.Item>
        <Descriptions.Item label={t('status')}>{project.project_status || '—'}</Descriptions.Item>
        <Descriptions.Item label={t('contractValue')}>
          {project.contract_value != null ? amt(project.contract_value) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('currency')}>{report.currency}</Descriptions.Item>
      </Descriptions>

      <Row gutter={16}>
        <Col span={printMode ? 6 : 24} md={6}>
          <Statistic title={t('revenuePeriod')} value={amt(pnl.revenue)} />
        </Col>
        <Col span={printMode ? 6 : 24} md={6}>
          <Statistic title={t('directCostPeriod')} value={amt(pnl.direct_cost)} />
        </Col>
        <Col span={printMode ? 6 : 24} md={6}>
          <Statistic
            title={t('grossProfit')}
            value={`${amt(pnl.gross_profit)}${pnl.gross_margin_pct != null ? ` (${formatPercent(pnl.gross_margin_pct)})` : ''}`}
            valueStyle={{ color: pnl.gross_profit < 0 ? '#cf1322' : '#389e0d' }}
          />
        </Col>
        <Col span={printMode ? 6 : 24} md={6}>
          <Statistic
            title={t('netProfit')}
            value={`${amt(pnl.net_profit)}${pnl.net_margin_pct != null ? ` (${formatPercent(pnl.net_margin_pct)})` : ''}`}
            valueStyle={{ color: pnl.net_profit < 0 ? '#cf1322' : '#389e0d' }}
          />
        </Col>
      </Row>

      {report.lines_truncated ? (
        <Alert type="warning" showIcon message={t('projectLedgerLinesTruncated')} />
      ) : null}

      {view === 'account' ? (
        <Table
          {...tableProps}
          rowKey="account_id"
          dataSource={report.accounts}
          columns={accountColumns}
          locale={{ emptyText: t('noProjectPostings') }}
          expandable={{
            defaultExpandAllRows: printMode,
            rowExpandable: (a) => (linesByAccount.get(a.account_id)?.length ?? 0) > 0,
            expandedRowRender: (a) => (
              <Table
                {...tableProps}
                rowKey="line_id"
                dataSource={linesByAccount.get(a.account_id) ?? []}
                columns={lineColumns}
              />
            ),
          }}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <strong>{t('total')}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <strong>{amt(report.total_debit)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                <strong>{amt(report.total_credit)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} />
            </Table.Summary.Row>
          )}
        />
      ) : (
        <Table
          {...tableProps}
          rowKey="line_id"
          dataSource={report.lines}
          columns={lineColumns}
          pagination={printMode ? false : { defaultPageSize: 100, showSizeChanger: true }}
          locale={{ emptyText: t('noProjectPostings') }}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <strong>{t('total')}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <strong>{amt(report.total_debit)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                <strong>{amt(report.total_credit)}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      )}
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t('projectLedgerAttributionHint')}
      </Text>
    </div>
  );
};

export default ProjectLedgerPage;
